import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

interface CountdownTimerProps {
  eventDate?: string | null;
  color?: string;
  labelColor?: string;
  size?: "md" | "sm";
}

function getTimeLeft(eventDate?: string | null) {
  if (!eventDate) return null;
  const targetDate = new Date(eventDate).getTime();
  const difference = targetDate - Date.now();

  if (Number.isNaN(targetDate) || difference <= 0) {
    return { days: "00", hours: "00", minutes: "00", seconds: "00" };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
}

export default function CountdownTimer({
  eventDate,
  color = "#fff",
  labelColor = "rgba(255,255,255,0.75)",
  size = "md",
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(eventDate));

  useEffect(() => {
    if (!eventDate) {
      setTimeLeft(null);
      return;
    }

    setTimeLeft(getTimeLeft(eventDate));
    const timer = setInterval(() => {
      const next = getTimeLeft(eventDate);
      setTimeLeft(next);
      if (!next || (next.days === "00" && next.hours === "00" && next.minutes === "00" && next.seconds === "00")) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate]);

  if (!timeLeft) return null;

  const units: Array<[string, string]> = [
    [timeLeft.days, "Days"],
    [timeLeft.hours, "Hrs"],
    [timeLeft.minutes, "Min"],
    [timeLeft.seconds, "Sec"],
  ];

  const isSmall = size === "sm";

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isSmall ? 1 : 2 }}>
      {units.map(([value, label]) => (
        <Box key={label} sx={{ textAlign: "center" }}>
          <Typography
            variant={isSmall ? "body1" : "h4"}
            sx={{ color, fontWeight: 700, lineHeight: 1 }}
          >
            {value}
          </Typography>
          <Typography variant={isSmall ? "caption" : "body2"} sx={{ color: labelColor }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
